import { TextLink } from 'braid-design-system';

import type { FAQ } from 'src/components/FaqAccordion/FaqAccordion';
import { createDotNetLinkUrl } from 'src/utils';

export const faqs: FAQ[] = [
  {
    question: 'When is the right time to sell my business?',
    answer:
      'There’s no perfect time to sell. You’re usually in a stronger position when your business is stable or growing, records are in good shape and it can run without relying entirely on you. Start earlier than you think, and consider your goals, market conditions and what you need to fix before listing.',
    link: {
      label: 'Read our guidance on assessing your readiness to sell.',
      href: createDotNetLinkUrl(
        '/business-resources/how-to-sell-a-business-the-ultimate-guide#assessing-your-readiness-to-sell',
      ),
    },
  },
  {
    question: 'How long will it take to sell my business?',
    answer:
      'Most businesses take 6–12 months to sell. High-demand businesses (such as healthcare and allied health services) may sell in about three months, while businesses in niche industries or remote/regional locations can take longer. Your sale timeframe depends on your price, buyer demand, and advertising strategy.',
    link: {
      label: 'Read our guide on how long it takes to sell a business.',
      href: createDotNetLinkUrl(
        '/business-resources/how-long-does-it-take-to-sell-a-business',
      ),
    },
  },
  {
    question: 'When should I sell my business myself vs use a broker?',
    answer:
      'It comes down to how much time, control and support you want. Selling privately means you won’t pay a broker commission, but you’ll handle ad creation, enquiries, negotiations and paperwork. A business broker can help if you’re a time-poor owner-operator, need access to specialised buyers, or want support from an expert to get the best outcome.',
    link: {
      label:
        'Read our guidance on selling yourself vs using a business broker.',
      href: createDotNetLinkUrl(
        '/business-resources/how-to-sell-a-business-the-ultimate-guide#choosing-how-to-sell-your-business',
      ),
    },
  },
  {
    question: 'How do I value my business?',
    answer:
      'Compare similar businesses for sale, then look at ongoing earnings, assets, goodwill, return on investment and future profitability. Allow for unusual or one-off costs so you’re judging typical performance. Don’t price emotionally or rely on one year’s result. An accountant, business adviser or certified valuer can help set a realistic price.',
    link: {
      label: 'Read our guidance on business appraisals and valuations.',
      href: createDotNetLinkUrl(
        '/business-resources/unlocking-the-true-value-of-a-business-a-guide-to-business-appraisals-and-valuations',
      ),
    },
  },
  {
    question:
      'What documents do I need to prepare before listing my business for sale?',
    answer:
      'Before you list, get key information together: three or more years of profit and loss statements, balance sheets, cash-flow statements, BAS (Business Activity Statement) and tax returns; forecasts; leases and supplier contracts; staff and operating records; asset and intellectual property details; and current licences. Requirements vary by state and sale structure, so get advice early.',
    link: {
      label: 'Use our checklist for selling a business.',
      href: createDotNetLinkUrl(
        '/business-resources/checklist-for-selling-a-business',
      ),
    },
  },
  {
    question:
      'How do I know which advertising package is right for my business?',
    answer:
      'Think about what you need. Testing early demand or working to a flexible timeline? Free is a good place to start. Want more visibility? Standard adds higher search placement and buyer recommendation emails. Want to find the right buyer, fast? Accelerate gives you the most exposure, with up to 6.8x more search appearances and 4.5x more views than Free ads.',
  },
  {
    question:
      'How can I keep my business sale private from staff and competitors?',
    answer: (
      <>
        If privacy is a concern, you can leave your business name out of the
        title and description and keep the ad high-level. Make sure you{' '}
        <TextLink
          href={createDotNetLinkUrl(
            '/business-resources/how-to-screen-potential-buyers',
          )}
        >
          screen buyers
        </TextLink>{' '}
        and release details in stages once a non-disclosure agreement (NDA) is
        signed. If privacy is critical, consider using a broker for more
        targeted outreach.
      </>
    ),
  },
  {
    question: 'Does SEEK Business charge commission?',
    answer: 'No, we do not charge commission and there are no hidden fees.',
  },
];
